import React from 'react';
import Modal from 'react-modal';
import { withTranslation, WithTranslation } from '@wix/wix-i18n-config';
import s from './PackageCycleModal.scss';
import X from '../../icons/dist/components/x';
import CycleList from './CycleList';
import {
  TaxTypes,
  TypeCycle,
  TypeSelectedOffer,
} from '../../types/general-types';
import {
  getQueryParam,
  navigateToUrl,
  removeDrilldownParams,
} from '../../services/window-utils';
import { biLogger } from '../../globals';
import {
  getBackPageUrl,
  getPurchasePackageURL,
  getSuccessPageURL,
} from '../../services/urls';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';

export interface PackageCycleModalProps
  extends WithTranslation,
    InjectedExperimentsProps {
  isOpen: boolean;
  onClose: Function;
  currency: string;
  cycles: TypeCycle[];
  selectedOffer: TypeSelectedOffer;
  productIndex: number;
  taxType?: TaxTypes;
}

interface PackageCycleModalState {
  selectedCycle: number;
}

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    width: '1100px',
    height: '550px',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#EFF3F7',
  },
  overlay: {
    backgroundColor: 'rgba(22, 45, 61, 0.66)',
    zIndex: 2,
  },
};

class PackageCycleModal extends React.Component<
  PackageCycleModalProps,
  PackageCycleModalState
> {
  constructor(props: PackageCycleModalProps) {
    super(props);
    const isDefaultEnabled =
      this.props.cycles && this.props.cycles[1]?.isEnabled;
    this.state = {
      selectedCycle: isDefaultEnabled ? 1 : 2,
    };
  }

  componentDidMount() {
    const param = getQueryParam().get('cycleId');
    if (param) {
      this.setState({
        selectedCycle: parseInt(param, 10),
      });
    }
  }

  modalOpen(props: PackageCycleModalProps) {
    document.body.style.overflow = 'hidden';
    const packageId = props.selectedOffer?.productId || '';
    const package_name = props.selectedOffer?.productName || '';
    biLogger.cycleOpen({ packageId, package_name });
  }

  modalClose() {
    document.body.style.overflow = 'unset';
  }

  onGotoPurchaseProduct = () => {
    const { selectedOffer, cycles, productIndex } = this.props;
    const selectedCycle = cycles[this.state.selectedCycle];
    const productId = selectedOffer.productId || '';
    const productName = selectedOffer.productName || '';
    const unit = selectedCycle.unit || '';
    const count = selectedCycle.count;
    const msid = selectedOffer.metaSiteId || '';
    const isChecked = this.state.selectedCycle === 1;
    const currentSubscription =
      selectedOffer.currentProduct && !selectedOffer.currentProduct.isBundled
        ? `&existingSubscriptionId=${selectedOffer.currentProduct.subscriptionId}`
        : '';
    const successPage = encodeURIComponent(
      getSuccessPageURL(msid, productId, unit, count),
    );
    const backPage = this.getBackUrl(productIndex, this.state.selectedCycle);
    const navigateUrl = this.getNavigateUrl(
      selectedOffer,
      productId,
      unit,
      `${count}`,
      currentSubscription,
      successPage,
      backPage,
    );
    navigateToUrl(navigateUrl);
    biLogger.cycleSelected({
      package_name: productName,
      packageId: productId,
      cycleId: `${count}`,
      cycleName: unit,
      isChecked,
    });
  };

  onSelectedCycle = (selectedCycle: number) => {
    removeDrilldownParams();
    this.setState({ selectedCycle });

    const selectedCycleElement = this.props.cycles[selectedCycle];
    const packageId = this.props.selectedOffer.productId || '';
    const package_name = this.props.selectedOffer.productName || '';
    const cycleName = selectedCycleElement.unit || '';
    const cycleId = `${selectedCycleElement.count}`;
    biLogger.cycleClick({ packageId, package_name, cycleName, cycleId });
  };

  private getNavigateUrl(
    selectedOffer: TypeSelectedOffer,
    productId: string,
    unit: string,
    count: string,
    currentSubscription: string,
    successPage: string,
    backPage: string,
  ) {
    return `${getPurchasePackageURL()}?siteId=${
      selectedOffer.metaSiteId
    }&productId=${productId}&quantity=1&cycleIntervalUnit=${unit}&cycleIntervalCount=${count}${currentSubscription}&successPageUrl=${successPage}&backPageUrl=${backPage}`;
  }

  getBackUrl(productIndex: number, cycleId: number) {
    return encodeURIComponent(getBackPageUrl(productIndex, cycleId));
  }

  render() {
    const { isOpen, onClose, cycles, currency, t, taxType } = this.props;
    const { selectedCycle } = this.state;

    return (
      <Modal
        isOpen={isOpen}
        style={customStyles}
        ariaHideApp={false}
        onAfterClose={this.modalClose}
        onAfterOpen={() => this.modalOpen(this.props)}
      >
        <div className={s.header} data-hook="packageCycleModal">
          <button
            className={s.closeButton}
            data-hook="packageCycleModal-closeButton"
            onClick={() => onClose()}
            tabIndex={0}
            aria-pressed="false"
            aria-label="Close"
          >
            <X size="14px" />
          </button>
        </div>
        <div className={s.title} data-hook="packageCycleModal-title">
          {t('packageCycleModal.title')}
        </div>
        <div className={s.cycleList} data-hook="packageCycleModal-cycleList">
          <CycleList
            cycles={cycles}
            currency={currency}
            selectedCycle={selectedCycle}
            onSelectedCycle={this.onSelectedCycle}
            taxType={taxType}
          />
        </div>
        <div className={s.selectButton}>
          <button
            data-hook="packageCycleModal-selectButton"
            onClick={this.onGotoPurchaseProduct}
            tabIndex={0}
            aria-pressed="false"
          >
            {t('packageCycleModal.selectButton')}
          </button>
        </div>
      </Modal>
    );
  }
}

export default withTranslation()(withExperiments(PackageCycleModal));
